/**
 * WetlabWorkflow Component
 *
 * Receives INIT { componentType: 'workflow', entityType: 'VERSION', entityId }
 * from Angular via postMessage, fetches version data, then renders the
 * maxGraph-based workflow editor (to be implemented).
 *
 * Communication flow:
 *   Angular → INIT { entityId, authToken }
 *   → fetchWetlabVersionInit(entityId)
 *   → render graph editor
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';

import { useEditorCommunication } from '@hooks/useEditorCommunication';
import { useToast } from '@hooks/useToast';
import {
  fetchWetlabVersionInit,
  fetchWetlabVersionGraphSnapshot,
  fetchWetlabVersionComments,
  saveWetlabVersion,
  saveAsWetlabVersion,
  updateProtocolLockStatus,
  fetchVersionDetails,
  createFolder,
  createWorkflow,
  fetchExperimentDetails,
  logAuditEvent,
  type WetlabEditorInitData,
} from '@services/editorApi.service';
import {
  createRunFromVersion,
  createRunExecute,
  createRunPlan,
} from '@services/runStageApi.service';
import { GraphEditorToolbar } from '@components/editor/GraphToolbar';
import { GraphFloatingControls } from '@components/editor/GraphFloatingControls';
import { SaveAsNewWorkflowDialog } from './SaveAsNewWorkflowDialog';
import { UnsavedChangesDialog } from './UnsavedChangesDialog';
import { VersionReasonModal } from '@components/VersionReasonModal/VersionReasonModal';
import { GraphEditorProvider } from '@/graph-react';
import { GraphEffects } from '@/graph-react/GraphEffects';
import { HighlightOverlays } from '@/graph-react/components/HighlightOverlays';
import { useGraphEditorStore, useSelectedGroupIds, wireGraphToStore } from '@/graph-store';
import { getEditBlockedMessage } from '@/utils/editBlockedMessages';
import type { GraphSnapshot } from '@/graph/domain/types';
import type { EntityExplorerConfig, EntityExplorerResult } from '@/types/entity-explorer.types';
import { ModalOutlet } from '@components/modals/ModalOutlet';
import { GraphEditorLayout } from '@components/editor/GraphEditorLayout';
import { LaunchRunDialog, type LaunchRunFormValue } from '@components/RunStage/LaunchRunDialog';
import styles from './WetlabWorkflow.module.css';

// =============================================================================
// CONSTANTS
// =============================================================================

const COMM_CONFIG = {
  targetOrigin: '*',
  debug: false,
};

// =============================================================================
// COMPONENT
// =============================================================================

type LoadState = 'idle' | 'loading' | 'loaded' | 'error';

export const WetlabWorkflow: React.FC = () => {
  // ── Communication ──────────────────────────────────────────────────────────
  const communication = useEditorCommunication(COMM_CONFIG);
  
  // ── Toast notifications ────────────────────────────────────────────────────
  const { showSuccess, showError, showWarning, showInfo } = useToast();

  // ── Local state ────────────────────────────────────────────────────────────
  const [loadState, setLoadState] = useState<LoadState>('idle');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [initData, setInitData] = useState<WetlabEditorInitData | null>(null);
  const [graphSnapshot, setGraphSnapshot] = useState<GraphSnapshot | null>(null);

  // Guard against stale async callbacks after unmount / reinit
  const cancelledRef = useRef(false);
  const lockToggleInFlightRef = useRef(false);

  // ── Store hooks (must be above early returns) ──────────────────────────────
  const openModal = useGraphEditorStore((s) => s.openModal);
  const closeModal = useGraphEditorStore((s) => s.closeModal);
  const zoomIn = useGraphEditorStore((s) => s.zoomIn);
  const zoomOut = useGraphEditorStore((s) => s.zoomOut);
  const zoomActual = useGraphEditorStore((s) => s.zoomActual);
  const zoomFit = useGraphEditorStore((s) => s.zoomFit);
  const collapseAllGroups = useGraphEditorStore((s) => s.collapseAllGroups);
  const expandAllGroups = useGraphEditorStore((s) => s.expandAllGroups);
  const setGroupsCollapsed = useGraphEditorStore((s) => s.setGroupsCollapsed);
  const selectedGroupIds = useSelectedGroupIds();
  const toSnapshot = useGraphEditorStore((s) => s.toSnapshot);
  const graphComments = useGraphEditorStore((s) => s.graphData.graphComments);
  const isDirty = useGraphEditorStore((s) => s.ui.isDirty);
  const clearDirty = useGraphEditorStore((s) => s.clearDirty);
  const seedStoreState = useGraphEditorStore((s) => s.seedStoreState);
  const hydrate = useGraphEditorStore((s) => s.hydrate);
  const setGraphComments = useGraphEditorStore((s) => s.setGraphComments);
  const addGraphComment = useGraphEditorStore((s) => s.addGraphComment);
  const setCurrentUser = useGraphEditorStore((s) => s.setCurrentUser);
  const setVersionId = useGraphEditorStore((s) => s.setVersionId);
  const setReadOnly = useGraphEditorStore((s) => s.setReadOnly);

  // ── Lock state from store ─────────────────────────────────────────────────────
  const isProtocolLocked = useGraphEditorStore((s) => s.ui.isProtocolLocked);
  const isLifecycleLocked = useGraphEditorStore((s) => s.ui.isLifecycleLocked ?? false);
  const isArchived = useGraphEditorStore((s) => s.ui.isArchived);
  const isArchivalInProgress = useGraphEditorStore((s) => s.ui.isArchivalInProgress);
  const runsExist = useGraphEditorStore((s) => s.ui.runsExist);
  const canEditContent = useGraphEditorStore((s) => s.ui.canEditContent ?? true);
  const lockReason = useGraphEditorStore((s) => s.ui.lockReason);
  const canToggleLock = useGraphEditorStore((s) => s.getCanToggleLock());
  const setLockState = useGraphEditorStore((s) => s.setLockState);

  // ── Save state ─────────────────────────────────────────────────────────────
  const [isSaving, setIsSaving] = useState(false);
  const [isLaunching, setIsLaunching] = useState(false);
  const [launchDialogOpen, setLaunchDialogOpen] = useState(false);
  const [isLockLoading, setIsLockLoading] = useState(false);
  const [saveAsWorkflowDialogOpen, setSaveAsWorkflowDialogOpen] = useState(false);
  const [unsavedChangesDialogOpen, setUnsavedChangesDialogOpen] = useState(false);
  const [selectedFolderForWorkflow, setSelectedFolderForWorkflow] = useState<any>(null);
  const [unsavedChangesFlow, setUnsavedChangesFlow] = useState<'discard' | 'saveCurrentVersion' | 'saveNewVersion' | null>(null);
  const [versionReasonModalOpen, setVersionReasonModalOpen] = useState(false);

  // ── Save handler ───────────────────────────────────────────────────────────
  const handleSave = useCallback(async () => {
    if (!initData || isSaving) return;
    const snapshot = toSnapshot();
    const allComments = graphComments.map((comment, idx) => ({
      comment: comment.comment || comment.stepId,
      entity: initData.version._id,
      commentId: comment.commentId ?? idx + 1,
      new: comment.new === true,
      addedBy: {
        _id: comment.addedBy?._id || initData.currentUser?._id,
        email: comment.addedBy?.email || initData.currentUser?.email || '',
        name: comment.addedBy?.name || initData.currentUser?.name || 'User',
        profilePhoto: comment.addedBy?.profilePhoto || initData.currentUser?.profilePhoto || null,
      },
      data: comment.data,
      type: 'GRAPH' as const,
      timestamp: comment.timestamp,
    }));
    setIsSaving(true);
    try {
      await saveWetlabVersion({
        _id: initData.version._id,
        uid: initData.version.uid,
        metadata: { name: initData.version.name },
        allComments,
        graph: snapshot,
        selectedNameType: undefined,
        isStepsOptimized: initData.version.isStepsOptimized,
      });
      setGraphComments(graphComments.map((c) => ({ ...c, new: false })));
      clearDirty();
      showSuccess('All changes saved', 5000, 'Version Updated');
      communication.sendMessage({ type: 'SAVED', payload: { versionId: initData.version._id } });
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'An error occurred while saving';
      console.error('[WetlabWorkflow] Save failed:', err);
      showError(message, 6000, 'Save Failed');
      communication.sendError('SAVE_FAILED', message, err);
    } finally {
      setIsSaving(false);
    }
  }, [initData, isSaving, toSnapshot, graphComments, setGraphComments, clearDirty, communication, showSuccess, showError]);

  // ── Save As handler ────────────────────────────────────────────────────────
  const handleSaveAsNewVersion = useCallback(() => {
    if (!initData || isSaving) return;


    // Check if version creation is disabled by lifecycle template
    const allowVersionCreationDisabled = initData.allowVersionCreationDisabled ?? false;
    if (allowVersionCreationDisabled) {
      showWarning(
        'Version creation is not allowed in the current lifecycle status. Contact your administrator.',
        6000,
        'Version Creation Disabled'
      );
      communication.sendError(
        'VERSION_CREATION_BLOCKED',
        'Cannot create new version - current lifecycle status does not allow version creation.',
        null
      );
      return;
    }

    // Check if entity is under state transition (approval pending)
    const isUnderTransition = initData.version?.lifecycleProperties?.isUnderTransition ?? false;
    console.log('[WetlabWorkflow] handleSaveAsNewVersion - isUnderTransition check:', {
      isUnderTransition,
      lifecycleProperties: initData.version?.lifecycleProperties,
    });
    if (isUnderTransition) {
      showWarning(
        'Cannot create new version while entity is under status transition. Please wait for the current transition to complete.',
        6000,
        'Status Transition In Progress'
      );
      communication.sendError(
        'SAVE_AS_BLOCKED',
        'Cannot create new version - entity is under status transition.',
        null
      );
      return;
    }

    // Validation: Check if control copy can create new version
    const isControlCopy = initData.version.experiment?.isControlCopy === true;
    const enableControlCopyVersionCreation =
      (initData.version.experiment as any)?.masterCopyProperties?.enableControlCopyVersionCreation === true;
    
    if (isControlCopy && !enableControlCopyVersionCreation) {
      console.warn('[WetlabWorkflow] Control copy version creation is not allowed');
      communication.sendError(
        'SAVE_AS_BLOCKED',
        'Control copy version creation is not allowed. Contact your administrator.',
        null
      );
      return;
    }

    setVersionReasonModalOpen(true);
  }, [initData, isSaving, communication]);

  const handleSaveAsNewVersionConfirm = useCallback(async (reason: string) => {
    if (!initData || isSaving) return;

    const snapshot = toSnapshot();
    setIsSaving(true);
    setVersionReasonModalOpen(false);
    try {
      const newVersion = await saveAsWetlabVersion({
        fromVersionId: initData.version._id,
        graph: snapshot,
        procedure: (initData.version as any)?.procedure || '',
        procedureMaterials: (initData.version as any)?.procedureMaterials || {},
        isStepsOptimized: initData.version.isStepsOptimized,
        versionReason: reason,
        allComments: graphComments.map((comment, idx) => ({
          comment: comment.comment || comment.stepId,
          entity: initData.version._id,
          commentId: comment.commentId ?? idx + 1,
          new: comment.new === true,
          addedBy: {
            _id: comment.addedBy?._id || initData.currentUser?._id,
            email: comment.addedBy?.email || initData.currentUser?.email || '',
            name: comment.addedBy?.name || initData.currentUser?.name || 'User',
            profilePhoto: comment.addedBy?.profilePhoto || initData.currentUser?.profilePhoto || null,
          },
          data: comment.data,
          type: 'GRAPH' as const,
          timestamp: comment.timestamp,
        })) || [],
      });
      
      clearDirty();
      const versionNumber = newVersion.versionNumber || 'new';
      showSuccess(`Created version v${versionNumber}`, 5000, 'New Version Created');
      
      // Verify we have a different version ID
      if (!newVersion._id || newVersion._id === initData.version._id) {
        showError('Failed to save as new version: Invalid response from server');
        return;
      }
      
      // Use the version label (v9, v10, etc) as the entityName for proper URL construction
      const versionLabel = `v${versionNumber}`;
      
      // Send navigate to Angular to update URL FIRST
      // This triggers Angular routing which will cause component to reload
      communication.sendNavigate('VERSION', newVersion._id, versionLabel);
      
      // Then fetch and load the new version data in background
      // If component unmounts due to navigation, the fresh component instance
      // will fetch data via the useEffect hook automatically
      setTimeout(async () => {
        try {
          const [newInitData, newGraphSnapshot] = await Promise.all([
            fetchWetlabVersionInit(newVersion._id),
            fetchWetlabVersionGraphSnapshot(newVersion._id),
          ]);
          
          // Update component state with new version (in case component is still mounted)
          setInitData(newInitData);
          setGraphSnapshot(newGraphSnapshot);
          setLoadState('loaded');
          
          // Reinitialize the graph store with new snapshot
          if (newGraphSnapshot) {
            seedStoreState(newGraphSnapshot);
            clearDirty();  // Clear dirty flag after loading new version
          }
          
          // Update store's version ID
          setVersionId(newVersion._id);
        } catch (loadErr) {
          // Navigation will handle it if this fails
        }
      }, 1500);
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'An error occurred while creating the new version';
      showError(message, 6000, 'Save As Failed');
      communication.sendError('SAVE_AS_FAILED', message, err);
    } finally {
      setIsSaving(false);
    }
  }, [
    initData, 
    isSaving, 
    toSnapshot, 
    clearDirty, 
    graphComments,
    communication, 
    showSuccess, 
    showError,
    seedStoreState,
    setVersionId,
    fetchWetlabVersionInit,
    fetchWetlabVersionGraphSnapshot,
  ]);

  const handleSaveAsNewVersionCancel = useCallback(() => {
    if (isSaving) return;
    setVersionReasonModalOpen(false);
  }, [isSaving]);

  // ── Handle lock toggle ────────────────────────────────────────────────────────
  const handleToggleLock = useCallback(async () => {
    if (!initData || !canToggleLock || lockToggleInFlightRef.current || isLockLoading) return;
    lockToggleInFlightRef.current = true;
    
    const versionId = initData.version._id;
    const versionUid = initData.version.uid;
    if (!versionUid) {
      showError('Unable to toggle lock: missing version uid');
      lockToggleInFlightRef.current = false;
      return;
    }
    
    setIsLockLoading(true);
    try {
      const newLockState = !isProtocolLocked;
      
      // Call API to update protocol lock status
      await updateProtocolLockStatus(versionId, versionUid, newLockState);
      
      // Fetch fresh version data
      const result = await fetchVersionDetails(versionId, versionUid);
      const updatedVersion = result.entity;
      console.log("updatedVersion", updatedVersion);
      
      // Update store with new lock state
      const updatedRunsExist = Array.isArray(updatedVersion.runs) && updatedVersion.runs.length > 0;
      const isArchivedNow = updatedVersion.isArchived ?? false;
      const isArchivalInProgressNow = updatedVersion.isArchivalInProgress ?? false;
      const protocolLockedNow = updatedVersion.protocolLocked ?? false;
      
      setLockState({
        isProtocolLocked: protocolLockedNow,
        isArchived: isArchivedNow,
        isArchivalInProgress: isArchivalInProgressNow,
        runsExist: updatedRunsExist,
      });
      
      // Update read-only state based on new lock status
      const shouldBeReadOnly = isArchivedNow || isArchivalInProgressNow || protocolLockedNow;
      setReadOnly(shouldBeReadOnly);
      
      showSuccess(newLockState ? 'Protocol is now locked' : 'Protocol is now unlocked', 4000, newLockState ? 'Protocol Locked' : 'Protocol Unlocked');
    } catch (error) {
      showError('Unable to update lock status. Please try again.', 5000, 'Lock Update Failed');
    } finally {
      setIsLockLoading(false);
      lockToggleInFlightRef.current = false;
    }
  }, [initData, canToggleLock, isProtocolLocked, isLockLoading, setLockState, setReadOnly, showSuccess, showError]);

  // ── Save As New Workflow handlers ──────────────────────────────────────────
  /**
   * Handler for initiating save as new workflow
   * 
   * If there are unsaved changes (isDirty), show the unsaved changes dialog with 3 options:
   * 1. Save to current version first → handleSave → then proceed to SaveAsNewWorkflowDialog
   * 2. Save as new version first → call save-as-new-version flow → then proceed
   * 3. Discard changes → proceed directly to SaveAsNewWorkflowDialog with last persisted state
   * 
   * If no unsaved changes, proceed directly to SaveAsNewWorkflowDialog
   */
  const handleSaveAsNewWorkflow = useCallback(() => {
    console.log('[DEBUG] handleSaveAsNewWorkflow: START', {
      allowVersionCreationDisabled: initData?.allowVersionCreationDisabled,
      isDirty,
      isProtocolLocked,
      isLifecycleLocked,
    });

    // Check if version creation is disabled by lifecycle template
    const allowVersionCreationDisabled = initData?.allowVersionCreationDisabled ?? false;
    if (allowVersionCreationDisabled) {
      showWarning(
        'Cannot create new procedure - version creation is not allowed in the current lifecycle status. Contact your administrator.',
        6000,
        'Procedure Creation Blocked'
      );
      console.warn('[WetlabWorkflow] Save As New Workflow disabled: lifecycle template does not allow version creation in current status');
      communication.sendError(
        'SAVE_AS_BLOCKED',
        'Cannot create new procedure - current lifecycle status does not allow version creation.',
        null
      );
      return;
    }

    // Check if entity is under state transition (approval pending)
    const isUnderTransition = initData?.version?.lifecycleProperties?.isUnderTransition ?? false;
    console.log('[WetlabWorkflow] handleSaveAsNewWorkflow - isUnderTransition check:', {
      isUnderTransition,
      lifecycleProperties: initData?.version?.lifecycleProperties,
    });
    if (isUnderTransition) {
      showWarning(
        'Cannot create new procedure while entity is under status transition. Please wait for the current transition to complete.',
        6000,
        'Status Transition In Progress'
      );
      communication.sendError(
        'SAVE_AS_BLOCKED',
        'Cannot create new procedure - entity is under status transition.',
        null
      );
      return;
    }

    console.log('[DEBUG] handleSaveAsNewWorkflow: Lifecycle check PASSED, proceeding');

    if (isDirty && !isProtocolLocked && !isLifecycleLocked) {
      // Show unsaved changes dialog
      setUnsavedChangesDialogOpen(true);
    } else {
      // No unsaved changes, proceed directly
      setSelectedFolderForWorkflow(null);
      setSaveAsWorkflowDialogOpen(true);
    }
  }, [isDirty, isProtocolLocked, isLifecycleLocked, initData, communication]);

  /**
   * Handle "Save to Current Version" option from unsaved changes dialog
   * Saves the current version, then proceeds to save-as-new-workflow with SAVED changes
   */
  const handleUnsavedChangesSaveCurrentVersion = useCallback(async () => {
    setUnsavedChangesDialogOpen(false);
    
    try {
      if (!initData || isSaving) return;

      const snapshot = toSnapshot();
      const allComments = graphComments.map((comment, idx) => ({
        comment: comment.comment || comment.stepId,
        entity: initData.version._id,
        commentId: comment.commentId ?? idx + 1,
        new: comment.new === true,
        addedBy: {
          _id: comment.addedBy?._id || initData.currentUser?._id,
          email: comment.addedBy?.email || initData.currentUser?.email || '',
          name: comment.addedBy?.name || initData.currentUser?.name || 'User',
          profilePhoto: comment.addedBy?.profilePhoto || initData.currentUser?.profilePhoto || null,
        },
        data: comment.data,
        type: 'GRAPH' as const,
        timestamp: comment.timestamp,
      }));

      // Save current version
      await saveWetlabVersion({
        _id: initData.version._id,
        uid: initData.version.uid,
        metadata: { name: initData.version.name },
        allComments,
        graph: snapshot,
        selectedNameType: undefined,
        isStepsOptimized: initData.version.isStepsOptimized,
      });

      setGraphComments(graphComments.map((c) => ({ ...c, new: false })));
      clearDirty();
      showSuccess('Changes saved to current version');
      
      // Now proceed to create new workflow dialog
      setUnsavedChangesFlow('saveCurrentVersion');
      setSelectedFolderForWorkflow(null);
      setSaveAsWorkflowDialogOpen(true);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Save failed';
      console.error('[UnsavedChanges] Failed to save current version:', err);
      showError(`Failed to save current version: ${message}`);
    }
  }, [initData, isSaving, toSnapshot, graphComments, setGraphComments, clearDirty, showSuccess, showError]);

  /**
   * Handle "Save as New Version" option from unsaved changes dialog
   * First saves as a new version in the CURRENT workflow, then proceeds to create new workflow
   */
  const handleUnsavedChangesSaveNewVersion = useCallback(async () => {
    setUnsavedChangesDialogOpen(false);
    
    // Open reason modal to ask why creating new version
    // The modal will then call handleVersionReasonConfirm with the reason
    setUnsavedChangesFlow('saveNewVersion');
    setVersionReasonModalOpen(true);
  }, []);

  /**
   * Handle "Discard" option from unsaved changes dialog
   * Discards unsaved changes and proceeds to save-as-new-workflow with LAST PERSISTED state
   */
  const handleUnsavedChangesDiscard = useCallback(() => {
    setUnsavedChangesDialogOpen(false);
    
    // Discard changes: clear isDirty and reset graph to last saved state
    clearDirty();
    if (graphSnapshot) {
      hydrate(graphSnapshot);
    }
    
    // Now proceed to create new workflow dialog with saved state
    setUnsavedChangesFlow('discard');
    setSelectedFolderForWorkflow(null);
    setSaveAsWorkflowDialogOpen(true);
  }, [graphSnapshot, clearDirty, hydrate]);

  /**
   * Handle explicit "Save as New Version" from toolbar
   * Opens the VersionReasonModal to capture reason for creating new version
   */
  /**
   * Handle confirmation from VersionReasonModal
   * Creates new version in current workflow with the provided reason
   */
  const handleVersionReasonConfirm = useCallback(
    async (reason: string) => {
      setVersionReasonModalOpen(false);
      if (!initData) {
        showError('Version data not loaded');
        return;
      }

      // Check if this is for unsaved changes "Save New Version" flow
      if (unsavedChangesFlow === 'saveNewVersion') {
        setIsSaving(true);
        try {
          if (!initData || isSaving) return;

          const snapshot = toSnapshot();
          const allComments = graphComments.map((comment, idx) => ({
            comment: comment.comment || comment.stepId,
            entity: initData.version._id,
            commentId: comment.commentId ?? idx + 1,
            new: comment.new === true,
            addedBy: {
              _id: comment.addedBy?._id || initData.currentUser?._id,
              email: comment.addedBy?.email || initData.currentUser?.email || '',
              name: comment.addedBy?.name || initData.currentUser?.name || 'User',
              profilePhoto: comment.addedBy?.profilePhoto || initData.currentUser?.profilePhoto || null,
            },
            data: comment.data,
            type: 'GRAPH' as const,
            timestamp: comment.timestamp,
          }));

          showInfo('Creating new version from current changes...');

          // Create new version with reason
          const newVersionResponse = await saveAsWetlabVersion({
            fromVersionId: initData.version._id,
            graph: snapshot,
            procedure: (initData.version as any)?.procedure || '',
            procedureMaterials: (initData.version as any)?.procedureMaterials || {},
            isStepsOptimized: initData.version.isStepsOptimized,
            versionReason: reason,
            allComments: allComments || [],
          });

          clearDirty();
          const newVersionNum = newVersionResponse.versionNumber || 'new';
          showSuccess(`New version created: v${newVersionNum}`);

          // Update initData and graph snapshot to use new version for workflow creation
          const newInitData = await fetchWetlabVersionInit(newVersionResponse._id);
          const newSnapshot = await fetchWetlabVersionGraphSnapshot(newVersionResponse._id);
          
          setInitData(newInitData);
          setGraphSnapshot(newSnapshot);
          
          // Now proceed to create new workflow dialog
          setSelectedFolderForWorkflow(null);
          setSaveAsWorkflowDialogOpen(true);
        } catch (err) {
          const message = err instanceof Error ? err.message : 'Failed to create new version';
          console.error('[SaveAsNewVersion] Failed:', err);
          showError(`Failed to create new version: ${message}`);
        } finally {
          setIsSaving(false);
        }
        return;  // Exit early for unsaved changes flow
      }

      // Regular "Save As New Version" flow
      setIsSaving(true);
      try {
        const snapshot = toSnapshot();
        const allComments = graphComments.map((comment, idx) => ({
          comment: comment.comment || comment.stepId,
          entity: initData.version._id,
          commentId: comment.commentId ?? idx + 1,
          new: comment.new === true,
          addedBy: {
            _id: comment.addedBy?._id || initData.currentUser?._id,
            email: comment.addedBy?.email || initData.currentUser?.email || '',
            name: comment.addedBy?.name || initData.currentUser?.name || 'User',
            profilePhoto: comment.addedBy?.profilePhoto || initData.currentUser?.profilePhoto || null,
          },
          data: comment.data,
          type: 'GRAPH' as const,
          timestamp: comment.timestamp,
        }));

        // Create new version directly using save-as API
        const newVersionResponse = await saveAsWetlabVersion({
          fromVersionId: initData.version._id,
          graph: snapshot,
          procedure: (initData.version as any)?.procedure || '',
          procedureMaterials: (initData.version as any)?.procedureMaterials || {},
          isStepsOptimized: initData.version.isStepsOptimized,
          versionReason: reason,
          allComments: graphComments?.map((comment, idx) => ({
            comment: comment.comment || comment.stepId,
            entity: initData.version._id,
            commentId: comment.commentId ?? idx + 1,
            new: comment.new === true,
            addedBy: {
              _id: comment.addedBy?._id || initData.currentUser?._id,
              email: comment.addedBy?.email || initData.currentUser?.email || '',
              name: comment.addedBy?.name || initData.currentUser?.name || 'User',
              profilePhoto: comment.addedBy?.profilePhoto || initData.currentUser?.profilePhoto || null,
            },
            data: comment.data,
            type: 'GRAPH' as const,
            timestamp: comment.timestamp,
          })) || [],
        });

        const versionNumber = newVersionResponse.versionNumber || 'new';
        showSuccess(`New version created: v${versionNumber}`);
        clearDirty();

        // Use the version label (v9, v10, etc) as the entityName for proper URL construction
        const versionLabel = `v${versionNumber}`;
        
        // Send navigate to Angular to update URL FIRST
        // This triggers Angular routing which will cause component to reload
        communication.sendNavigate('VERSION', newVersionResponse._id, versionLabel);
        
        // Then fetch and load the new version data in background
        // If component unmounts due to navigation, the fresh component instance
        // will fetch data via the useEffect hook automatically
        setTimeout(async () => {
          try {
            const [newInitData, newGraphSnapshot] = await Promise.all([
              fetchWetlabVersionInit(newVersionResponse._id),
              fetchWetlabVersionGraphSnapshot(newVersionResponse._id),
            ]);
            
            // Update component state with new version (in case component is still mounted)
            setInitData(newInitData);
            setGraphSnapshot(newGraphSnapshot);
            setLoadState('loaded');
            
            // Reinitialize the graph store with new snapshot
            if (newGraphSnapshot) {
              seedStoreState(newGraphSnapshot);
              clearDirty();  // Clear dirty flag after loading new version
            }
            
            // Update store's version ID
            setVersionId(newVersionResponse._id);
          } catch (loadErr) {
            // Navigation will handle it if this fails
          }
        }, 1500);
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Failed to create new version';
        console.error('[SaveAsNewVersion] Failed:', err);
        showError(`Failed to create new version: ${message}`);
      } finally {
        setIsSaving(false);
      }
    },
    [
      initData, 
      selectedFolderForWorkflow,
      unsavedChangesFlow,
      isSaving,
      toSnapshot, 
      graphComments, 
      clearDirty, 
      showInfo, 
      showError, 
      showSuccess,
      communication,
      seedStoreState,
      setVersionId,
      fetchWetlabVersionInit,
      fetchWetlabVersionGraphSnapshot,
      createFolder,
      createWorkflow,
      fetchExperimentDetails,
    ]
  );

  /**
   * Handle cancel from VersionReasonModal
   */
  const handleVersionReasonCancel = useCallback(() => {
    setVersionReasonModalOpen(false);
  }, []);

  const handleOpenFolderSelectorForWorkflow = useCallback((forPath: boolean = false) => {
    const config: EntityExplorerConfig = {
      mode: 'select-folder',
      entityTypes: [],
      title: forPath ? 'Select Folder Location for New Folder' : 'Select Folder',
    };
    openModal({
      kind: 'entity-explorer',
      payload: {
        config,
        onConfirm: (result: EntityExplorerResult) => {
          if (result.kind === 'folder' && result.selectedFolder) {
            if (forPath) {
              // For "Create New Folder": Store folder path
              const pathStr = result.folderPath
                .map((node) => node.name)
                .filter((n) => n)
                .join(' > ');
              setSelectedFolderForWorkflow({
                path: pathStr || 'Root',
                isForPath: true,
              });
            } else {
              // For "Add to existing": Store folder object with name
              setSelectedFolderForWorkflow({
                name: result.selectedFolder.name,
                _id: result.selectedFolder._id,
                isForPath: false,
              });
            }
          }
        },
      },
    });
  }, [openModal]);

  const handleSaveAsWorkflowConfirm = useCallback(
    async (data: {
      fileType: string;
      name: string;
      folderOption: 'existing' | 'new';
      folderName?: string;
      folderPath?: string;
    }) => {
      if (!initData || !selectedFolderForWorkflow) {
        showError('Missing procedure or folder information');
        return;
      }

      setIsSaving(true);
      try {
        const graphToUse = toSnapshot();
        
        // Get project ID from experiment
        let projectId = initData.version.experiment?.project;
        
        if (!projectId && initData.parentExperimentId) {
          try {
            const experimentDetails = await fetchExperimentDetails(initData.parentExperimentId);
            projectId = experimentDetails.project;
          } catch (err) {
            console.error('[SaveAsNewWorkflow] Failed to fetch experiment details:', err);
          }
        }
        
        if (!projectId) {
          showError('Unable to determine project. Please refresh and try again.');
          return;
        }

        let targetFolderId = selectedFolderForWorkflow._id;

        // Step 1: Create new folder if needed
        if (data.folderOption === 'new') {
          try {
            const folderResponse = await createFolder({
              _id: selectedFolderForWorkflow._id,
              dataFileType: 'FLEXI_FOLDER',
              parentEntityType: 'FOLDER',
              dataFileName: data.folderName || 'New Folder',
              parentId: selectedFolderForWorkflow._id,
              isNotSubFolder: true,
            });
            
            targetFolderId = folderResponse._id;
            showSuccess(`Folder "${folderResponse.dataFileName || folderResponse.name || 'New Folder'}" created successfully`);
          } catch (err) {
            const message = err instanceof Error ? err.message : 'Failed to create folder';
            console.error('[SaveAsNewWorkflow] Folder creation failed:', {
              error: err,
              folderName: data.folderName,
              parentId: selectedFolderForWorkflow._id,
            });
            showError(`Failed to create folder: ${message}`);
            return;
          }
        }

        // Step 2: Create workflow in target folder
        try {
          // Extract source workflow context for audit logging
          const sourceWorkflowName = initData.version.experiment?.name || initData.version.name || 'Procedure';
          const sourceVersionNumber = initData.version.versionNumber || 1;
          const sourceVersionId = initData.version._id;
          
          const workflowResponse = await createWorkflow({
            projectId,
            fileType: data.fileType,
            parentEntityType: 'FLEXI_FOLDER',
            parentEntityId: targetFolderId,
            name: data.name,
            versionData: {
              _id: sourceVersionId, // For audit enricher to lookup source version details
              name: `v1 - ${data.name}`,
              procedureMaterials: (initData.version as any)?.procedureMaterials || {},
              procedure: (initData.version as any)?.procedure || '',
              allComments: graphComments
                .filter((c) => !!c.stepId && !!c.data)
                .map((comment, idx) => ({
                  comment: comment.comment || comment.stepId,
                  entity: initData.version._id,
                  commentId: comment.commentId ?? idx + 1,
                  addedBy: {
                    _id: comment.addedBy?._id || initData.currentUser?._id,
                    email: comment.addedBy?.email || initData.currentUser?.email || '',
                    name: comment.addedBy?.name || initData.currentUser?.name || 'User',
                    profilePhoto: comment.addedBy?.profilePhoto || initData.currentUser?.profilePhoto || null,
                  },
                  data: comment.data,
                  type: 'GRAPH',
                  timestamp: comment.timestamp,
                })) || [],
              graph: (graphToUse as any) || {
                materialNodes: [],
                operationNodes: [],
                flowEdges: [],
                groups: [],
              },
              experiment: {
                _id: initData.version.experiment?._id,
                name: initData.version.experiment?.name,
              },
            },
            isControlCopy: false,
            auditCreateSource: 'save_as_new_workflow',
            // Additional context for audit description
            sourceWorkflowName,
            sourceVersionNumber,
          });

          if (!workflowResponse || !workflowResponse._id) {
            console.error('[SaveAsNewWorkflow] Invalid response from server:', workflowResponse);
            showError('Failed to create procedure: Invalid response from server');
            return;
          }
          
          // Get the version ID from the response
          // versions array contains version IDs as strings directly
          const versionId = workflowResponse.versions?.[0];
          
          if (!versionId) {
            console.error('[SaveAsNewWorkflow] No version found in response:', workflowResponse);
            showError('Failed to create procedure: Could not determine version ID');
            return;
          }
          
          const workflowId = workflowResponse._id;
          const versionLabel = `v1`;

          showSuccess(`Procedure "${workflowResponse.name}" created successfully`);

          // Close ALL modals before navigation
          setSaveAsWorkflowDialogOpen(false);
          setSelectedFolderForWorkflow(null);
          setUnsavedChangesFlow(null);
          closeModal();

          // Navigate to workflow versions page, not editor
          // URL format: /folders/WETLABWORKFLOW/{workflowId}/{versionId}/versions
          communication.sendNavigate('WETLABWORKFLOW', workflowId, versionLabel);
        } catch (err) {
          const message = err instanceof Error ? err.message : 'Failed to create procedure';
          console.error('[SaveAsNewWorkflow] Workflow creation failed:', err);
          showError(`Failed to create procedure: ${message}`);
        }
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Save As failed';
        console.error('[SaveAsNewWorkflow] Error:', err);
        showError(`Failed to create procedure: ${message}`);
      } finally {
        setIsSaving(false);
      }
    },
    [
      initData,
      selectedFolderForWorkflow,
      toSnapshot,
      graphComments,
      showSuccess,
      showError,
      fetchExperimentDetails,
      createFolder,
      createWorkflow,
      closeModal,
      communication,
    ]
  );

  const handleSaveAsWorkflowCancel = useCallback(() => {
    setSaveAsWorkflowDialogOpen(false);
    setSelectedFolderForWorkflow(null);
  }, []);

  useEffect(() => {
    if (!communication.initPayload) return;

    const { entityId } = communication.initPayload;
    cancelledRef.current = false;

    // Clear old state before loading new version
    setInitData(null);
    setGraphSnapshot(null);
    setLoadState('loading');
    setErrorMessage(null);
    clearDirty();  // Reset dirty flag for new version load

    Promise.all([
      fetchWetlabVersionInit(entityId),
      fetchWetlabVersionGraphSnapshot(entityId),
    ])
      .then(([data, snapshot]) => {
        if (cancelledRef.current) return;
        setInitData(data);
        setGraphSnapshot(snapshot);
        setLoadState('loaded');
      })
      .catch((err: Error) => {
        if (cancelledRef.current) return;
        console.error('[WetlabWorkflow] Failed to load version:', err);
        setErrorMessage(err.message || 'Failed to load version details.');
        communication.sendError('INIT_FAILED', err.message || 'Failed to load version details.', err);
        setLoadState('error');
      });

    return () => {
      cancelledRef.current = true;
    };
  }, [communication.initPayload, communication.initSequence, clearDirty]);

  // ── Set current user and lock state in store ──────────────────────────────
  useEffect(() => {
    if (initData?.currentUser) {
      setCurrentUser({
        _id: initData.currentUser._id,
        name: initData.currentUser.name,
        email: initData.currentUser.email,
        profilePhoto: initData.currentUser.profilePhoto,
      });
    } else {
      setCurrentUser(null);
    }

    if (initData?.version?._id) {
      setVersionId(initData.version._id);
    }

    // Initialize lock state from version data
    console.log("initData lockInfo", initData?.lockInfo);
    if (initData?.lockInfo) {
      setLockState({
        isProtocolLocked: initData.lockInfo.isProtocolLocked ?? false,
        isLifecycleLocked: initData.lockInfo.isLifecycleLocked ?? false,
        isArchived: initData.lockInfo.isArchived ?? false,
        isArchivalInProgress: initData.lockInfo.isArchivalInProgress ?? false,
        runsExist: initData.lockInfo.runsExist ?? false,
        canEditContent: initData.lockInfo.canEditContent ?? true,
      });
    }

  }, [initData?.currentUser?._id, initData?.version?._id, initData?.lockInfo, setCurrentUser, setVersionId, setLockState]);

  // ── Set read-only state based on lock/archive status ──────────────────────
  useEffect(() => {
    if (!initData?.lockInfo) return;
    
    // Read-only when archived or protocol is locked or lifecycle is locked
    const isReadOnlyMode = 
      initData.lockInfo.isArchived || 
      initData.lockInfo.isProtocolLocked || 
      initData.lockInfo.isLifecycleLocked ||
      !initData.lockInfo.canEditContent;
    
    setReadOnly(isReadOnlyMode);
  }, [initData?.lockInfo?.isArchived, initData?.lockInfo?.isProtocolLocked, initData?.lockInfo?.isLifecycleLocked, initData?.lockInfo?.canEditContent, setReadOnly]);

  // ── Load comments for refresh persistence ─────────────────────────────────
  useEffect(() => {
    if (!initData?.version?._id) return;

    let cancelled = false;
    const loadComments = async () => {
      try {
        const comments = await fetchWetlabVersionComments(initData.version._id);
        if (cancelled) return;

        const mapped = (comments || [])
          .filter((c) => (c.type || '').toUpperCase() === 'VERSION' || (c.type || '').toUpperCase() === 'GRAPH')
          .map((c, idx) => ({
            id: c._id || String(idx + 1),
            _id: c._id,
            comment: c.comment || '',
            entity: initData.version._id,
            commentId: idx + 1,
            new: false,
            data: typeof c.data === 'string' ? c.data : '',
            stepId: c.comment || '',
            addedBy: {
              _id: c.addedBy?._id,
              name: c.addedBy?.name || 'User',
              email: c.addedBy?.email || '',
              profilePhoto: c.addedBy?.profilePhoto || null,
            },
            timestamp: typeof c.timestamp === 'number' ? c.timestamp : new Date(c.timestamp || Date.now()).getTime(),
            type: 'GRAPH' as const,
          }))
          .filter((c) => !!c.stepId && !!c.data);

        // Get current comments and merge (preserves test comments, deduplicates by _id)
        const currentComments = useGraphEditorStore.getState().graphData.graphComments;
        const testComments = currentComments.filter((c) => c.id?.startsWith('test-'));
        
        // Deduplicate: Create a set of API comment IDs (only those that have _id)
        const apiCommentIds = new Set(mapped.filter((c) => !!c._id).map((c) => c._id!));
        
        // Filter out any existing comments that are now in mapped (API comments)
        const existingNonApiComments = testComments.filter((c) => !c._id || !apiCommentIds.has(c._id));
        
        // Merge: existing test comments + fresh API comments
        const mergedComments = [...existingNonApiComments, ...mapped];
        setGraphComments(mergedComments);
      } catch {
        if (!cancelled) {
          // On error, preserve existing comments
          const currentComments = useGraphEditorStore.getState().graphData.graphComments;
          if (currentComments.length === 0) setGraphComments([]);
        }
      }
    };

    loadComments();
    return () => {
      cancelled = true;
    };
  }, [initData?.version?._id, setGraphComments]);

  // ── Retry handler ──────────────────────────────────────────────────────────
  const handleRetry = useCallback(() => {
    if (!communication.initPayload) return;
    setLoadState('idle');
    setErrorMessage(null);
    setInitData(null);
    setGraphSnapshot(null);
  }, [communication.initPayload]);

  const buildSourceNodes = useCallback((snapshot: GraphSnapshot) => {
    const incomingTargets = new Set(snapshot.flowEdges.map((edge) => edge.targetId));

    const sources = snapshot.materialNodes.filter((node) => {
      if (!node.id) return false;
      return !incomingTargets.has(node.id);
    });

    const sourceNodes = (sources.length > 0 ? sources : snapshot.materialNodes)
      .filter((node) => !!node.id)
      .map((node) => ({
        nodeId: String(node.id),
        nodeName: node.name,
        materialName: node.name,
        containerName: node.container?.name || '',
      }));

    return sourceNodes;
  }, []);

  const handleLaunchRun = useCallback(async () => {
    if (!initData || isLaunching) return;

    // Guard: Check if version is archived
    if (isArchived) {
      showWarning('Cannot launch run: Version is archived');
      return;
    }

    // Validation: Check if control copy can create new run (one run restriction)
    const isControlCopy = initData.version.experiment?.isControlCopy === true;
    const restrictOneRun =
      (initData.version.experiment as any)?.masterCopyProperties?.restrictOneRunForEachControlCopy === true;

    // Prefer runs array; fallback to runsCount only if runs doesn't exist
    const runsCount =
      initData.version.runs && Array.isArray(initData.version.runs)
        ? initData.version.runs.length
        : (initData.version as any)?.runsCount ?? 0;
    
    if (restrictOneRun && isControlCopy && runsCount > 0) {
      console.warn(
        '[WetlabWorkflow] Control copy already has a run. Only one run per control copy version is allowed.'
      );
      communication.sendError(
        'LAUNCH_RUN_BLOCKED',
        `Control copy already has ${runsCount} run(s). Only one run per version is allowed.`,
        null
      );
      return;
    }

    const snapshot = toSnapshot();
    const allComments = graphComments.map((comment, idx) => ({
      comment: comment.comment || comment.stepId,
      entity: initData.version._id,
      commentId: comment.commentId ?? idx + 1,
      new: comment.new === true,
      addedBy: {
        _id: comment.addedBy?._id || initData.currentUser?._id,
        email: comment.addedBy?.email || initData.currentUser?.email || '',
        name: comment.addedBy?.name || initData.currentUser?.name || 'User',
        profilePhoto: comment.addedBy?.profilePhoto || initData.currentUser?.profilePhoto || null,
      },
      data: comment.data,
      type: 'GRAPH' as const,
      timestamp: comment.timestamp,
    }));

    try {
      await saveWetlabVersion({
        _id: initData.version._id,
        uid: initData.version.uid,
        metadata: { name: initData.version.name },
        allComments,
        graph: snapshot,
        selectedNameType: undefined,
        isStepsOptimized: initData.version.isStepsOptimized,
      });
      clearDirty();  // Clear dirty flag after save
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'An error occurred while saving';
      showError(message, 6000, 'Save Failed');
      communication.sendError('LAUNCH_RUN_SAVE_FAILED', message, err);
      return;
    }

    setLaunchDialogOpen(true);
  }, [initData, isLaunching, isArchived, toSnapshot, graphComments, showError, showWarning, communication]);

  const handleLaunchCancel = useCallback(() => {
    if (isLaunching) return;
    setLaunchDialogOpen(false);
  }, [isLaunching]);

  const handleLaunchSubmit = useCallback(async (form: LaunchRunFormValue) => {
    if (!initData || !graphSnapshot || isLaunching) return;

    const snapshot = toSnapshot();
    const sourceNodes = buildSourceNodes(snapshot);
    if (sourceNodes.length === 0) {
      const emptyProtocolMessage = 'An empty protocol cannot be launched. Please add steps to the protocol before launching.';
      setLaunchDialogOpen(false);
      showWarning(emptyProtocolMessage);
      communication.sendError('LAUNCH_RUN_BLOCKED', emptyProtocolMessage, null);
      return;
    }

    setIsLaunching(true);
    try {
      const assignees =
        form.assigneeIds.length > 0
          ? form.assigneeIds
          : initData.currentUser?._id
            ? [initData.currentUser._id]
            : [];

      const run = await createRunFromVersion({
        version: initData.version._id,
        experiment: initData.parentExperimentId,
        name: 'run',
        assignees,
        startDate: form.startDate ? new Date(form.startDate).toISOString() : undefined,
        endDate: form.endDate ? new Date(form.endDate).toISOString() : undefined,
        tat:
          form.tatValue && form.tatUnit
            ? {
                value: form.tatValue,
                unit: form.tatUnit,
              }
            : undefined,
      });

      if (!run?._id) {
        throw new Error('Run creation failed: missing run id');
      }

      await createRunExecute({
        runId: run._id,
        runUid: run.uid,
        nodes: sourceNodes,
        startTime: form.startDate ? new Date(form.startDate).toISOString() : null,
        endTime: form.endDate ? new Date(form.endDate).toISOString() : null,
        duration: '',
      });

      await createRunPlan({
        runId: run._id,
        runUid: run.uid,
        nodes: sourceNodes,
        originalNodes: sourceNodes,
        startTime: form.startDate ? new Date(form.startDate).toISOString() : null,
        endTime: form.endDate ? new Date(form.endDate).toISOString() : null,
        duration: '',
        typeOfExecution: '',
      });

      setLaunchDialogOpen(false);
      // Store success message in session storage for run page to display
      sessionStorage.setItem('runLaunchMessage', 'Run launched successfully');
      communication.sendNavigate('RUN', run._id, 'Run created');
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'An error occurred while launching the run';
      showError(message, 6000, 'Launch Failed');
      communication.sendError('LAUNCH_RUN_FAILED', message, err);
    } finally {
      setIsLaunching(false);
    }
  }, [initData, graphSnapshot, isLaunching, toSnapshot, buildSourceNodes, communication, showError, showWarning]);

  // ── States ─────────────────────────────────────────────────────────────────
  if (!communication.isReady || loadState === 'idle') {
    return (
      <div className={styles.container}>
        <div className={styles.loadingWrapper}>
          <div className={styles.spinner} />
          <span>Connecting…</span>
        </div>
      </div>
    );
  }

  if (loadState === 'loading') {
    return (
      <div className={styles.container}>
        <div className={styles.loadingWrapper}>
          <div className={styles.spinner} />
          <span>Loading procedure…</span>
        </div>
      </div>
    );
  }

  if (loadState === 'error' || !initData || !graphSnapshot) {
    return (
      <div className={styles.container}>
        <div className={styles.errorWrapper}>
          <p className={styles.errorTitle}>Failed to load procedure</p>
          <p className={styles.errorMessage}>{errorMessage}</p>
          <button className={styles.retryButton} onClick={handleRetry}>
            Retry
          </button>
        </div>
      </div>
    );
  }

  // ── Graph editor ───────────────────────────────────────────────────────────
  const { version, permissions, lockInfo, currentUser, fileType } = initData;

  const handleAddMaterial = () => {
    if (isProtocolLocked || isLifecycleLocked || isArchived || isArchivalInProgress || runsExist || !canEditContent) {
      showWarning(getEditBlockedMessage({
        isProtocolLocked,
        isLifecycleLocked,
        canEditContent,
        isArchived,
        isArchivalInProgress,
        runsExist,
      }));
      return;
    }
    openModal({ kind: 'add-material' });
  };

  const handleImportWorkflow = () => {
    if (isProtocolLocked || isLifecycleLocked || isArchived || isArchivalInProgress || runsExist || !canEditContent) {
      showWarning(getEditBlockedMessage({
        isProtocolLocked,
        isLifecycleLocked,
        isArchived,
        isArchivalInProgress,
        runsExist,
        canEditContent,
      }));
      return;
    }
    showInfo('Opening procedure browser');
    const config: EntityExplorerConfig = {
      mode: 'import',
      entityTypes: ['WETLABWORKFLOW'],
      title: 'Import Procedure',
    };
    openModal({
      kind: 'entity-explorer',
      payload: {
        config,
        onConfirm: handleImportWorkflowConfirm,
      },
    });
  };

  /**
   * Handle duplicate materials when importing workflow
   * Materials are referenced by ID, not by name, so duplicate names are safe
   * Just add all imported materials as-is - no renaming needed
   */
  const addImportedMaterialsWithoutConflict = (
    importedMaterials: GraphSnapshot['materialNodes'],
  ): {
    materialsToAdd: GraphSnapshot['materialNodes'];
  } => {
    // Simply return all imported materials without any renaming
    // Each material has a unique ID, so duplicate names don't cause graph conflicts
    // No-op: materials are uniquely keyed by ID; avoid console logging in production.

    return {
      materialsToAdd: importedMaterials,
    };
  };

  const handleImportWorkflowConfirm = async (result: EntityExplorerResult) => {
    // Ensure we have the right result type
    if (!result || result.kind !== 'import' || !result.items || result.items.length === 0) {
      return;
    }

    const importedVersion = result.items[0];
    if (!importedVersion || !importedVersion._id) {
      showError('Invalid procedure selected');
      return;
    }

    // Must have current workflow loaded
    if (!initData) {
      showError('No procedure loaded to import into');
      return;
    }

    showInfo(`Importing procedure: ${importedVersion.preFillName}`);

    try {
      // Fetch only the imported workflow's graph (not its lock state)
      // We will merge this graph into the current workflow
      const importedSnapshot = await fetchWetlabVersionGraphSnapshot(importedVersion._id);
      
      if (!importedSnapshot) {
        showError('Failed to fetch procedure data');
        return;
      }

      // Get CURRENT graph state from store (not the stale graphSnapshot)
      // This includes any unsaved materials/steps added by the user
      const currentSnapshot = toSnapshot();

      // Calculate max creationOrder from current operations
      const currentOps = currentSnapshot.operationNodes || [];
      const maxExistingOrder = currentOps.length > 0
        ? Math.max(...currentOps.map(op => op.creationOrder || 0))
        : 0;

      // Offset imported operation nodes' creationOrder
      // Keep relative order: offset by maxExistingOrder
      const offsetImportedOps = (importedSnapshot.operationNodes || []).map(op => ({
        ...op,
        creationOrder: (op.creationOrder || 0) + maxExistingOrder,
      }));

      // Add all imported materials (no renaming needed - unique IDs prevent conflicts)
      const { 
        materialsToAdd
      } = addImportedMaterialsWithoutConflict(
        importedSnapshot.materialNodes || []
      );

      // Merge imported graph nodes/edges into current graph
      // This mirrors Angular's import behavior - adds imported content to current workflow
      const mergedSnapshot: GraphSnapshot = {
        materialNodes: [
          ...(currentSnapshot.materialNodes || []),
          ...materialsToAdd,
        ],
        operationNodes: [
          ...currentOps,
          ...offsetImportedOps,
        ],
        flowEdges: [
          ...(currentSnapshot.flowEdges || []),
          ...(importedSnapshot.flowEdges || []),
        ],
        groups: [
          ...(currentSnapshot.groups || []),
          ...(importedSnapshot.groups || []),
        ],
        schemaVersion: currentSnapshot.schemaVersion || '2.0',
      };

      // Update React state with merged snapshot (for fallback in case graph re-renders)
      setGraphSnapshot(mergedSnapshot);
      
      // Capture current lock state before hydrate (hydrate resets ui)
      const lockState = {
        isProtocolLocked,
        isArchived,
        isArchivalInProgress,
        runsExist,
      };

      // Hydrate both store AND maxGraph visualization with merged snapshot
      // This will re-render the graph canvas immediately
      hydrate(mergedSnapshot);
      
      // Restore lock state (hydrate() resets ui to defaults)
      // Set isDirty to true because the graph has been modified by the import
      useGraphEditorStore.setState((state) => ({
        ui: {
          ...state.ui,
          ...lockState,
          isDirty: true, // Mark as dirty so user can save the imported workflow
        }
      }));
      
      // Preserve current comments if any
      if (graphComments.length > 0) {
        setGraphComments(graphComments);
      }

      // Log audit event immediately for import workflow action
      try {
        const versionLabel = initData.version.versionNumber 
          ? `v${initData.version.versionNumber}` 
          : 'v1';
        const wfLabel = initData.fileType?.toUpperCase().includes('COMPUTATIONAL') 
          ? 'Computational Pipeline'
          : 'Wet-lab Procedure';
        const workflowName = initData.version.experiment?.name || initData.version.name || 'Procedure';

        await logAuditEvent({
          actionType: 'UPDATE',
          entityType: wfLabel,
          entityId: initData.version._id,
          entityUID: initData.version.uid || '',
          entityName: workflowName,
          description: `${workflowName} ${wfLabel} ${versionLabel} was updated.`,
          pageRoute: window.location.pathname,
          entityDetails: {
            name: workflowName,
            fileType: initData.fileType,
            versionNumber: initData.version.versionNumber,
            versionName: versionLabel,
          },
        });
      } catch (auditErr) {
        console.error('[WetlabWorkflow] Failed to log import audit event:', auditErr);
        // Continue even if audit logging fails
      }

      showSuccess(`Procedure "${importedVersion.preFillName}" imported successfully`);
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to import procedure';
      console.error('[WetlabWorkflow] Import failed:', error);
      showError(`Failed to import procedure: ${message}`);
    }
  };

  const handleImportFiles = () => {
    if (isProtocolLocked || isLifecycleLocked || isArchived || isArchivalInProgress || runsExist || !canEditContent) {
      showWarning(getEditBlockedMessage({
        isProtocolLocked,
        isLifecycleLocked,
        isArchived,
        isArchivalInProgress,
        runsExist,
        canEditContent,
      }));
      return;
    }
    showInfo('Importing files');
    // TODO: Implement file import
  };

  const handleImportData = () => {
    if (isProtocolLocked || isLifecycleLocked || isArchived || isArchivalInProgress || runsExist || !canEditContent) {
      showWarning(getEditBlockedMessage({
        isProtocolLocked,
        isLifecycleLocked,
        isArchived,
        isArchivalInProgress,
        runsExist,
        canEditContent,
      }));
      return;
    }
    showInfo('Importing data');
    // TODO: Implement data import
  };

  const handleDeleteSelected = () => {
    if (isProtocolLocked || isLifecycleLocked || isArchived || isArchivalInProgress || runsExist || !canEditContent) {
      showWarning(getEditBlockedMessage({
        isProtocolLocked,
        isLifecycleLocked,
        isArchived,
        isArchivalInProgress,
        runsExist,
        canEditContent,
      }));
      return;
    }
    showInfo('Deleting selected elements');
    // TODO: Implement delete operation
  };

  // No-op callbacks — wired incrementally as features land
  const noop = () => {};

  const toolbarPermissions = {
    canSave: permissions.save,
    canSaveAsNewVersion: permissions.saveAs,
    canSaveAsNewWorkflow: permissions.saveAs,
    canImport: permissions.import,
    canLaunchRun: permissions.launch,
    canLock: permissions.lock,
    canEdit: !lockInfo.isLocked,
  };

  return (
    <div className={styles.container}>
      {/* Toolbar sits outside the Provider so it always renders above the canvas */}
      <GraphEditorToolbar
        versionName={version.name ?? ''}
        versionNumber={version.versionNumber ?? 1}
        workflowName={version.experiment?.name ?? fileType}
        isDirty={isDirty}
        isLocked={false}
        isArchived={isArchived}
        isArchivalInProgress={isArchivalInProgress}
        isLaunching={isLaunching}
        isProtocolLocked={isProtocolLocked || isLifecycleLocked}
        canEditContent={canEditContent}
        canToggleLock={canToggleLock}
        lockReason={lockReason}
        isLockLoading={isLockLoading}
        allowVersionCreationDisabled={initData?.allowVersionCreationDisabled ?? false}
        permissions={toolbarPermissions}
        onSave={handleSave}
        onSaveAsNewVersion={handleSaveAsNewVersion}
        onSaveAsNewWorkflow={handleSaveAsNewWorkflow}
        onImportMaterial={handleAddMaterial}
        onImportWorkflow={handleImportWorkflow}
        onImportFiles={handleImportFiles}
        onImportData={handleImportData}
        onUndo={noop}
        onRedo={noop}
        onDeleteSelected={handleDeleteSelected}
        onSelectAll={noop} 
        onAddMaterial={handleAddMaterial}
        onCollapseGroups={selectedGroupIds.length > 0 ? () => setGroupsCollapsed(selectedGroupIds, true) : collapseAllGroups}
        onExpandGroups={selectedGroupIds.length > 0 ? () => setGroupsCollapsed(selectedGroupIds, false) : expandAllGroups}
        collapseGroupsLabel={selectedGroupIds.length > 0 ? `Collapse Selection (${selectedGroupIds.length})` : 'Collapse All'}
        expandGroupsLabel={selectedGroupIds.length > 0 ? `Expand Selection (${selectedGroupIds.length})` : 'Expand All'}
        onZoomIn={zoomIn}
        onZoomOut={zoomOut}
        onZoomActual={zoomActual}
        onZoomFit={zoomFit}
        onToggleMilestones={noop}
        onToggleTranslationPanel={noop}
        onLaunchRun={handleLaunchRun}
        onToggleLock={handleToggleLock}
      />

      {/*
        GraphEditorLayout provides: canvas area | drag handle | right panel
        (Protocol / Info tabs, resizable, persisted width).
        The outer div fills remaining flex space inside the column container.
      */}
      <div style={{ flex: 1, minHeight: 0 }}>
        <GraphEditorLayout>
          <GraphEditorProvider
            key={version._id}
            initialSnapshot={graphSnapshot}
            onReady={(graph) => {
              wireGraphToStore(graph);
              seedStoreState(graphSnapshot);
              clearDirty();  // Clear dirty flag when graph editor is ready
            }}
            containerClassName="graph-canvas-scroll"
            containerStyle={{
              position: 'absolute',
              inset: 0,
              overflow: 'auto',
              padding: '20px',
            }}
          >
            <ModalOutlet />
            <GraphEffects />
            <HighlightOverlays />
          </GraphEditorProvider>

          <GraphFloatingControls
            isLocked={isProtocolLocked || isLifecycleLocked}
            isArchived={isArchived}
            isArchivalInProgress={isArchivalInProgress}
            canImport={permissions.import}
            onImportMaterial={handleAddMaterial}
            onImportWorkflow={handleImportWorkflow}
            onZoomIn={zoomIn}
            onZoomOut={zoomOut}
            onZoomFit={zoomFit}
            onZoomActual={zoomActual}
          />
        </GraphEditorLayout>
      </div>

      <LaunchRunDialog
        open={launchDialogOpen}
        busy={isLaunching}
        entityId={initData.parentExperimentId || initData.version.experiment?._id || initData.version._id}
        entityType={initData.fileType}
        defaultAssigneeIds={initData.currentUser?._id ? [initData.currentUser._id] : []}
        onCancel={handleLaunchCancel}
        onSubmit={handleLaunchSubmit}
        onSuccess={() => showSuccess('Run launched successfully')}
      />

      <SaveAsNewWorkflowDialog
        open={saveAsWorkflowDialogOpen}
        onConfirm={handleSaveAsWorkflowConfirm}
        onCancel={handleSaveAsWorkflowCancel}
        onSelectFolder={handleOpenFolderSelectorForWorkflow}
        selectedFolderName={selectedFolderForWorkflow?.name}
        selectedFolderPath={selectedFolderForWorkflow?.path}
      />

      <UnsavedChangesDialog
        open={unsavedChangesDialogOpen}
        onCurrentVersion={handleUnsavedChangesSaveCurrentVersion}
        onNewVersion={handleUnsavedChangesSaveNewVersion}
        onDiscard={handleUnsavedChangesDiscard}
      />

      <VersionReasonModal
        isOpen={versionReasonModalOpen}
        title="Create New Version"
        placeholder="Enter reason for creating this new version..."
        confirmBtnText="Create Version"
        cancelBtnText="Cancel"
        onConfirm={handleVersionReasonConfirm}
        onCancel={handleVersionReasonCancel}
      />
    </div>
  );
};
